import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Mail, MapPin, Music } from "lucide-react";
import { ARTIST, BIO, EP, PHOTOS, EPK_PDF } from "../mock";

export default function Presentation() {
  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16" data-testid="presentation-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">Accueil</Link>
        <span>/</span>
        <span className="text-white">Présentation</span>
      </div>

      {/* TITLE */}
      <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            01 · L'artiste
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Vince <span className="text-blue-600">Rodi</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">Auteur · Compositeur · Interprète</span>
          </div>
          {/* Citation / phrase d'accroche */}
          <blockquote className="mt-8 font-serif-accent italic text-2xl md:text-4xl lg:text-5xl leading-tight text-neutral-100 max-w-3xl">
            <span className="text-red-500">«</span> Tu n'me connais pas&nbsp;? <br className="hidden md:block" />
            <span className="text-blue-500">Alors je viens à toi.</span> <span className="text-red-500">»</span>
          </blockquote>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <div className="flex flex-wrap gap-2">
            {["Pop", "Variété française", "Auteur-compositeur", "PACA"].map((t) => (
              <span key={t} className="text-xs uppercase tracking-[0.2em] border border-white/20 px-3 py-1.5 text-neutral-300">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="grid md:grid-cols-12 gap-10 lg:gap-14">
        <aside className="md:col-span-5 lg:col-span-4">
          <div className="aspect-[4/5] overflow-hidden bg-neutral-900 mb-6">
            <img src={PHOTOS.portraitBW} alt="Vince Rodi" className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="aspect-square overflow-hidden bg-neutral-900">
              <img src={PHOTOS.portraitSofa} alt="Vince Rodi portrait" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square overflow-hidden bg-neutral-900">
              <img src={PHOTOS.stageRedBlue} alt="Vince Rodi scène" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* EPK Download */}
          <a
            href={EPK_PDF}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="epk-download-btn"
            className="mt-6 group flex items-center justify-between border border-white/20 hover:border-red-500/60 hover:bg-white/[0.02] transition-all px-5 py-4"
          >
            <div className="flex items-center gap-3">
              <Download size={18} className="text-red-500 group-hover:translate-y-0.5 transition-transform" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Press kit</div>
                <div className="text-sm text-neutral-100 font-medium">EPK Vince Rodi — 2026</div>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">PDF</span>
          </a>
        </aside>

        <div className="md:col-span-7 lg:col-span-8">
          <h2 className="font-serif-accent italic text-3xl md:text-4xl leading-tight text-neutral-100 mb-8">
            « Une pop sincère, portée par les histoires de la vie. »
          </h2>
          <div className="space-y-5 text-neutral-300 leading-relaxed max-w-2xl">
            {BIO.long.map((p, i) => (
              <p key={i} className="text-[15px] md:text-base">{p}</p>
            ))}
          </div>

          {/* FACTS */}
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {[
              { k: "Origine", v: ARTIST.origin },
              { k: "Style", v: "Pop · Chanson" },
              { k: "Label", v: ARTIST.label },
            ].map((f) => (
              <div key={f.k} className="border-t border-white/15 pt-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{f.k}</div>
                <div className="mt-2 font-display text-2xl">{f.v}</div>
              </div>
            ))}
          </div>

          {/* CONTACT BLOCK */}
          <div className="mt-12 border border-white/15 p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Booking & contact pro</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <a href={`mailto:${ARTIST.email}`} className="flex items-start gap-3 group">
                <Mail size={18} className="text-neutral-400 mt-0.5 group-hover:text-white" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Email artiste</div>
                  <div className="text-sm group-hover:text-white">{ARTIST.email}</div>
                </div>
              </a>
              <a href={`mailto:${ARTIST.labelEmail}`} className="flex items-start gap-3 group">
                <Music size={18} className="text-neutral-400 mt-0.5 group-hover:text-white" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{ARTIST.label}</div>
                  <div className="text-sm group-hover:text-white">{ARTIST.labelEmail}</div>
                </div>
              </a>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Basé à</div>
                  <div className="text-sm">PACA & Bourgogne-Franche-Comté — France</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EP HIGHLIGHT */}
      <div className="mt-24 border-t border-white/10 pt-14">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-3">— EP</div>
            <h3 className="font-display text-6xl md:text-7xl uppercase leading-[0.9]">{EP.title}</h3>
            <div className="mt-3 text-sm uppercase tracking-[0.2em] text-neutral-500">{EP.type}</div>
            <a
              href={ARTIST.linktree}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 px-5 py-3 text-xs uppercase tracking-[0.2em]"
            >
              Écouter sur toutes les plateformes <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="md:col-span-7">
            <p className="text-neutral-300 leading-relaxed mb-8">{EP.description}</p>
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {EP.tracks.map((t) => (
                <li key={t.n} className="py-5 flex items-start gap-5">
                  <span className="font-display text-2xl text-neutral-500 w-10">{t.n}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl uppercase tracking-wide">{t.title}</span>
                      {t.tag && (
                        <span className="text-[10px] uppercase tracking-[0.2em] bg-white text-black px-2 py-0.5">
                          {t.tag}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-400 mt-1.5 max-w-2xl">{t.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
